import {
	Chip,
	CircularProgress,
	IconButton,
	Menu,
	MenuItem as MuiMenuItem,
} from "@mui/material";
import { useEffect, useState, useCallback, useMemo, memo } from "react";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import "./styles.css";
import { JobStatusEnum } from "@/lib/types/job";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import {
	JobListItem,
	ChannelListItem,
	ChannelListItemContent,
} from "./job-list-components/job-list-item.component";
import {
	Archive,
	CreditCard,
	LayoutDashboard,
	TicketCheck,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { MenuItemButton } from "@/components/src/menu-item-button/menu-item-button.component";
import { JobDocumentTypeEnum } from "../../pages/job-page/job-document/job-document.types";
import {
	JobListEntry,
	JobListJob,
	JobRequestChannel,
	JobStage,
} from "@/lib/types/job-list.types";
import { useCentralStore } from "../../store/Central";

const JOB_STATUS_OPTIONS: {
	status: JobStatusEnum;
	label: string;
	stage?: JobStage;
}[] = [
	// {
	// 	status: JobStatusEnum.PRE_REGISTERED,
	// 	label: "Voranmeldungen",
	// },
	{
		status: JobStatusEnum.REQUESTED,
		label: "Auftragseingang",
	},
	{
		status: JobStatusEnum.IN_PROGRESS,
		label: "Neu",
		stage: "empty",
	},
	{
		status: JobStatusEnum.IN_PROGRESS,
		label: "Kostenvoranschläge",
		stage: JobDocumentTypeEnum.Quotation,
	},
	{
		status: JobStatusEnum.IN_PROGRESS,
		label: "Lieferscheine",
		stage: JobDocumentTypeEnum.DeliveryNote,
	},
	{
		status: JobStatusEnum.IN_PROGRESS,
		label: "Gutschriften",
		stage: JobDocumentTypeEnum.CreditNote,
	},
	{
		status: JobStatusEnum.COMPLETED,
		label: "Geliefert",
	},
];

export interface JobListProps {
	entityId: string;
	basePath: "/client" | "/vendor";
	jobList: Record<JobStatusEnum, JobListEntry>;
	jobRequestChannels: JobRequestChannel[];
	selectedJobStatus: JobStatusEnum | null;
	selectedJobStage: JobStage | null;
	onChangeSelectedStatusOrStage: (
		status: JobStatusEnum | null,
		stage: JobStage | null
	) => void;
	onFetchJobsByStatus: (status: JobStatusEnum) => Promise<void>;
	onRefresh?: () => void;
	showQuickActions?: boolean;
}

export const JobList: React.FC<JobListProps> = ({
	entityId,
	basePath,
	jobList,
	jobRequestChannels,
	selectedJobStatus,
	selectedJobStage,
	onChangeSelectedStatusOrStage,
	onFetchJobsByStatus,
	onRefresh,
	showQuickActions = true,
}) => {
	const navigate = useNavigate();
	const memoizedJobLists = useMemo(
		() =>
			JOB_STATUS_OPTIONS.map((option) => (
				<MemoizedSingleJobList
					key={option.label}
					entityId={entityId}
					basePath={basePath}
					jobList={jobList}
					jobRequestChannels={
						option.status === JobStatusEnum.REQUESTED &&
						!option.stage
							? jobRequestChannels
							: []
					}
					selectedJobStatus={selectedJobStatus}
					selectedJobStage={selectedJobStage}
					onChangeSelectedStatusOrStage={
						onChangeSelectedStatusOrStage
					}
					onFetchJobsByStatus={onFetchJobsByStatus}
					label={option.label}
					status={option.status}
					stage={option.stage ?? null}
					onRefresh={onRefresh}
				/>
			)),
		[
			entityId,
			basePath,
			jobList,
			jobRequestChannels,
			selectedJobStatus,
			selectedJobStage,
			onChangeSelectedStatusOrStage,
			onFetchJobsByStatus,
			onRefresh,
		]
	);

	return (
		<div className="flex flex-col gap-1 pt-2 text-sm">
			<MenuItemButton
				style={{
					display: "flex",
					flexDirection: "row",
					justifyContent: "start",
					alignItems: "center",
					gap: "8px",
				}}
				selected={location.pathname === `${basePath}/${entityId}`}
				onClick={() => {
					navigate(`${basePath}/${entityId}`);
				}}
				aria-label="Übersicht"
				data-testid="job-list-nav-dashboard"
			>
				<LayoutDashboard className="w-4 h-4" />
				<span className="text-sm">Übersicht</span>
			</MenuItemButton>
			{memoizedJobLists}
			{showQuickActions && (
				<JobPagesList basePath={basePath} entityId={entityId} />
			)}
		</div>
	);
};

const dateFilters = [
	{
		label: "Alle",
		value: 0,
	},
	{
		label: "1 Jahr",
		value: 1,
	},
	{
		label: "2 Jahre",
		value: 2,
	},
	{
		label: "3 Jahre",
		value: 3,
	},
	{
		label: "4 Jahre",
		value: 4,
	},
	{
		label: "5 Jahre",
		value: 5,
	},
];

interface SingleJobListProps {
	entityId: string;
	basePath: "/client" | "/vendor";
	jobList: Record<JobStatusEnum, JobListEntry>;
	jobRequestChannels: JobRequestChannel[];
	selectedJobStatus: JobStatusEnum | null;
	selectedJobStage: JobStage | null;
	onChangeSelectedStatusOrStage: (
		status: JobStatusEnum | null,
		stage: JobStage | null
	) => void;
	onFetchJobsByStatus: (status: JobStatusEnum) => Promise<void>;
	label: string;
	status: JobStatusEnum;
	stage: JobStage | null;
	onRefresh?: () => void;
}

const SingleJobList: React.FC<SingleJobListProps> = ({
	entityId,
	basePath,
	jobList,
	jobRequestChannels,
	selectedJobStatus,
	selectedJobStage,
	onChangeSelectedStatusOrStage,
	onFetchJobsByStatus,
	label,
	status,
	stage,
	onRefresh,
}) => {
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const openFilter = Boolean(anchorEl);
	const [filterIsLoading, setFilterIsLoading] = useState(false);
	const [jobsLoading, setJobsLoading] = useState(false);
	const { jobId: urlJobId } = useParams<{ jobId: string }>();

	// On the client side the global jobRequestChannels state may briefly contain
	// channels from the previously-selected client between switch and refetch.
	// Filter by the current client's connected_organization_id to never render
	// another org's channels. The vendor side has no equivalent filter — it owns
	// all channels for the current vendor by construction.
	const connectedOrgId = useCentralStore(
		(s) => s.client?.connected_organization_id ?? null
	);
	const filteredJobRequestChannels = useMemo(() => {
		if (basePath !== "/client") return jobRequestChannels;
		if (!connectedOrgId) return [];
		return jobRequestChannels.filter(
			(ch) => ch.customerOrgId === connectedOrgId
		);
	}, [jobRequestChannels, basePath, connectedOrgId]);

	const handleToggleList = useCallback(
		(status: JobStatusEnum, stage: JobStage | null) => {
			if (selectedJobStatus === status && selectedJobStage === stage) {
				onChangeSelectedStatusOrStage(null, null);
			} else {
				onChangeSelectedStatusOrStage(status, stage);
			}
		},
		[selectedJobStatus, selectedJobStage, onChangeSelectedStatusOrStage]
	);

	const currentFilterValue = useCentralStore(
		(state) => state.searchConfig?.statuses?.[status]
	);

	const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	}, []);

	const asyncFetchJobsByStatus = async (status: JobStatusEnum) => {
		setJobsLoading(true);
		await onFetchJobsByStatus(status);
		setJobsLoading(false);
	};

	useEffect(() => {
		asyncFetchJobsByStatus(status);
	}, [entityId, status]);

	const handleClose = useCallback(() => {
		setAnchorEl(null);
	}, []);

	const handleFilter = useCallback(
		async (filter: number) => {
			setFilterIsLoading(true);
			setAnchorEl(null);
			const { searchConfig, updateSearchConfig } =
				useCentralStore.getState();
			await updateSearchConfig({
				statuses: {
					...(searchConfig?.statuses as Record<
						JobStatusEnum,
						number
					>),
					[status]: filter,
				},
			});
			setFilterIsLoading(false);
		},
		[status]
	);

	const { jobs, jobCount } = useMemo(() => {
		let jobs: JobListJob[] = [];
		let jobCount = 0;

		const entry = jobList[status];
		if (stage && entry?.stages) {
			const stageData = entry.data[stage];
			jobs = stageData?.jobs ?? [];
			jobCount = stageData?.jobCount ?? 0;
		} else if (entry && !entry.stages) {
			jobs = entry.data?.jobs ?? [];
			jobCount = entry.data?.jobCount ?? 0;
		}

		return { jobs, jobCount };
	}, [jobList, status, stage]);

	const totalCount = jobCount + filteredJobRequestChannels.length;

	return (
		<>
			<div className="flex flex-row items-center justify-between">
				<div className="flex flex-row items-center">
					<IconButton
						aria-label="open"
						size="small"
						onClick={() => handleToggleList(status, stage)}
					>
						{selectedJobStatus == status &&
						selectedJobStage == stage ? (
							<ArrowDropDownIcon fontSize="small" />
						) : (
							<ArrowRightIcon fontSize="small" />
						)}
					</IconButton>
					<div
						className="flex flex-row items-center cursor-pointer"
						onContextMenu={(e) => {
							e.preventDefault();
							handleClick(e);
						}}
						aria-haspopup="true"
						aria-expanded={openFilter ? "true" : "false"}
						title="Rechtsklick für Zeitfilter"
						onClick={() => {
							handleToggleList(status, stage);
						}}
					>
						{label}
						{totalCount > 0 && !jobsLoading && (
							<Chip
								label={totalCount}
								size="small"
								style={{
									marginLeft: "6px",
									fontSize: "12px",
								}}
							/>
						)}
						{jobsLoading && (
							<CircularProgress
								size={10}
								style={{
									marginLeft: "6px",
								}}
							/>
						)}
					</div>
				</div>
				<div style={{ position: "relative" }}>
					{filterIsLoading && (
						<IconButton disabled>
							<AutorenewIcon fontSize="small" />
						</IconButton>
					)}
					<Menu
						id="basic-menu"
						anchorEl={anchorEl}
						open={openFilter}
						onClose={handleClose}
						MenuListProps={{
							"aria-labelledby": "basic-button",
						}}
					>
						{dateFilters.map((filter) => (
							<MuiMenuItem
								onClick={() => handleFilter(filter.value)}
								key={filter.label}
								sx={{
									backgroundColor:
										currentFilterValue === filter.value
											? "var(--neutral-100)"
											: "white",
								}}
							>
								{filter.label}
							</MuiMenuItem>
						))}
					</Menu>
				</div>
			</div>
			{selectedJobStatus === status && selectedJobStage === stage && (
				<div
					style={{
						maxHeight: "70vh",
						overflowY: "auto",
					}}
				>
					{!jobsLoading && (
						<>
							{filteredJobRequestChannels.map((channel) => (
								<ChannelListItem
									key={channel.channelId}
									channelId={channel.channelId}
									entityId={entityId}
									basePath={basePath}
								>
									<ChannelListItemContent channel={channel} />
								</ChannelListItem>
							))}
							{jobs.map((job) => (
								<JobListItem
									key={job.id}
									basePath={basePath}
									entityId={entityId}
									jobId={job.id ?? 0}
									jobStatus={
										job.status as unknown as JobStatusEnum
									}
									jobTitle={job.title ?? ""}
									jobCode={job.code ?? ""}
									patientId={job.patient_id ?? ""}
									sharedWith={!!job.shared_with}
									isSelected={
										Number(urlJobId) === (job.id ?? 0)
									}
									onRefresh={onRefresh}
								/>
							))}
						</>
					)}
				</div>
			)}
		</>
	);
};

const MemoizedSingleJobList = memo(SingleJobList, (prevProps, nextProps) => {
	return (
		prevProps.label === nextProps.label &&
		prevProps.status === nextProps.status &&
		prevProps.stage === nextProps.stage &&
		prevProps.onRefresh === nextProps.onRefresh &&
		prevProps.entityId === nextProps.entityId &&
		prevProps.basePath === nextProps.basePath &&
		prevProps.selectedJobStatus === nextProps.selectedJobStatus &&
		prevProps.selectedJobStage === nextProps.selectedJobStage &&
		prevProps.jobList === nextProps.jobList &&
		prevProps.jobRequestChannels === nextProps.jobRequestChannels
	);
});

interface JobPagesListProps {
	basePath: "/client" | "/vendor";
	entityId: string;
}

const JobPagesList: React.FC<JobPagesListProps> = ({ basePath, entityId }) => {
	const navigate = useNavigate();
	return (
		<div className="flex flex-col gap-1 pt-2">
			<MenuItemButton
				style={{
					display: "flex",
					flexDirection: "row",
					justifyContent: "start",
					alignItems: "center",
					gap: "8px",
				}}
				selected={location.pathname.includes("/booked")}
				onClick={() => {
					navigate(`${basePath}/${entityId}/booked`);
				}}
			>
				<TicketCheck className="w-4 h-4" />
				<span className="text-sm">Verbucht</span>
			</MenuItemButton>
			<MenuItemButton
				style={{
					display: "flex",
					flexDirection: "row",
					justifyContent: "start",
					alignItems: "center",
					gap: "8px",
				}}
				selected={location.pathname.includes("/paid")}
				onClick={() => {
					navigate(`${basePath}/${entityId}/paid`);
				}}
			>
				<CreditCard className="w-4 h-4" />
				<span className="text-sm">Bezahlt</span>
			</MenuItemButton>
			<MenuItemButton
				style={{
					display: "flex",
					flexDirection: "row",
					justifyContent: "start",
					alignItems: "center",
					gap: "8px",
				}}
				selected={location.pathname.includes("/archive")}
				onClick={() => {
					navigate(`${basePath}/${entityId}/archive`);
				}}
			>
				<Archive className="w-4 h-4" />
				<span className="text-sm">Archiviert</span>
			</MenuItemButton>
		</div>
	);
};
